import { useState, useEffect, useCallback, useRef } from "react";
import { BridgeEvent, NativeBridgeAvailable } from "@unu/ui-enums";
import { sendEvent } from "@unu/ui-common";
import { registerEvents } from "@unu/web-native-bridge";
import { setNativeAppVersion } from "@/services/native-app-version";
import type { SDKDataType } from "@/health/healthDataTypes";

/** Occurrence count per native error code, for the page session. */
const healthErrorCodeCounts = new Map<string, number>();

export type { SDKDataType } from "@/health/healthDataTypes";

/**
 * Health record from native platform
 */
export interface HealthRecord {
  platform: "healthkit" | "health_connect";
  sourceId: string;
  dataType: SDKDataType;
  value: number;
  unit: string;
  timestamp: string;
  endTimestamp?: string;
  source: string;
  metadata?: Record<string, unknown>;
}

/**
 * Permission status for a data type
 */
export interface DataTypePermission {
  type: SDKDataType;
  read: "not_determined" | "authorized" | "denied" | "restricted" | "limited";
  write?: "not_determined" | "authorized" | "denied" | "restricted" | "limited";
}

/**
 * Health permissions state
 */
export interface HealthPermissions {
  dataTypes: DataTypePermission[];
  allGranted: boolean;
  hasAnyGranted: boolean;
  hasDenied: boolean;
  lastChecked: string;
}

/**
 * Health error
 */
export interface HealthError {
  code: string;
  message: string;
  action?: string;
}

export const PlatformType = {
  HEALTHKIT: "healthkit",
  HEALTH_CONNECT: "health_connect",
} as const;

export type Platform = (typeof PlatformType)[keyof typeof PlatformType];

const nativePackageKeys = [
  "axios",
  "binah",
  "reactNative",
  "expo",
  "webView",
  "firebaseMessaging",
] as const;

export type NativePackageVersions = Partial<
  Record<(typeof nativePackageKeys)[number], string>
>;

function normalizeNativePackageVersions(
  value: unknown
): NativePackageVersions | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const versions: NativePackageVersions = {};
  for (const key of nativePackageKeys) {
    const version = (value as Record<string, unknown>)[key];
    if (typeof version === "string" && version.trim()) {
      versions[key] = version.trim();
    }
  }
  return versions;
}

/**
 * Health platform status
 */
export interface HealthStatus {
  platform: Platform;
  isConnected: boolean;
  isAvailable: boolean;
  isLoading: boolean;
  isFetching: boolean;
  permissions: HealthPermissions | null;
  error: HealthError | null;
  isSamsungHealthInstalled: boolean;
  nativeAppVersion: string | null;
  nativeBuildNumber: string | null;
  binahNativeSdkVersion: string | null;
  nativePackageVersions?: NativePackageVersions | null;
}

/**
 * Options for fetching health data
 */
export interface FetchHealthDataOptions {
  dataTypes?: SDKDataType[];
  startDate?: Date;
  endDate?: Date;
  limit?: number;
}

/**
 * Hook return type
 */
export interface UseNativeHealthSDKResult {
  // Platform state
  status: HealthStatus | null;
  isAvailable: boolean;
  isConnected: boolean;
  hasAnyPermissions: boolean;
  isLoading: boolean;
  permissions: HealthPermissions | null;
  error: HealthError | null;
  isSamsungHealthInstalled: boolean;

  // Data state
  records: HealthRecord[];
  isFetching: boolean;

  // Actions
  requestPermissions: (dataTypes?: SDKDataType[]) => void;
  fetchHealthData: (options?: FetchHealthDataOptions) => void;
  refreshStatus: () => void;
  openHealthConnectSettings: () => void;
  openAppPermissionSettings: () => void;
  revokeAllPermissions: () => void;

  // Helpers
  getRecordsByType: (dataType: SDKDataType) => HealthRecord[];
  getLatestRecord: (dataType: SDKDataType) => HealthRecord | null;
  clearRecords: () => void;
}

/**
 * useNativeHealthSDK
 *
 * Hook for web app to interact with native health SDK (HealthKit/Health Connect)
 * via the webview bridge.
 *
 * @example
 * ```tsx
 * const {
 *   isConnected,
 *   isAvailable,
 *   permissions,
 *   records,
 *   isFetching,
 *   requestPermissions,
 *   fetchHealthData,
 * } = useNativeHealthSDK();
 *
 * // Request permissions (snake_case SDK types for native bridge)
 * requestPermissions(['heart_rate', 'steps', 'weight']);
 *
 * // Fetch data
 * fetchHealthData({
 *   dataTypes: ['heart_rate', 'steps'],
 *   startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
 *   endDate: new Date(),
 * });
 * ```
 */
export function useNativeHealthSDK(): UseNativeHealthSDKResult {
  const [status, setStatus] = useState<HealthStatus | null>(null);
  const [records, setRecords] = useState<HealthRecord[]>([]);
  const [error, setError] = useState<HealthError | null>(null);
  // Last known availability/connection, read by the error handler for context.
  const prevStatusRef = useRef<{ isAvailable: boolean; isConnected: boolean }>({
    isAvailable: false,
    isConnected: false,
  });

  const handleStatusUpdate = useCallback((data: any) => {
    if (data.nativeAppVersion) {
      setNativeAppVersion(data.nativeAppVersion);
    }

    setStatus({
      platform: data.platform,
      isConnected: data.isConnected ?? false,
      isAvailable: data.isAvailable ?? false,
      isLoading: data.isLoading ?? false,
      isFetching: data.isFetching ?? false,
      permissions: data.permissions ?? null,
      error: data.error ?? null,
      isSamsungHealthInstalled: data.isSamsungHealthInstalled ?? false,
      nativeAppVersion: data.nativeAppVersion ?? null,
      nativeBuildNumber: data.nativeBuildNumber ?? null,
      binahNativeSdkVersion: data.binahNativeSdkVersion ?? null,
      nativePackageVersions: normalizeNativePackageVersions(
        data.nativePackageVersions
      ),
    });

    prevStatusRef.current = {
      isAvailable: data.isAvailable ?? false,
      isConnected: data.isConnected ?? false,
    };

    setError(data.error ?? null);
  }, []);

  const handleDataReceived = useCallback((data: any) => {
    if (data.records && Array.isArray(data.records)) {
      setRecords(data.records);
    }

    if (data.error) {
      setError(data.error);
    }
  }, []);

  const handlePermissionsResult = useCallback((data: any) => {
    if (data.permissions) {
      setStatus((prev) =>
        prev
          ? {
              ...prev,
              permissions: data.permissions,
              isConnected: data.permissions.hasAnyGranted,
            }
          : null
      );
    }

    setError(data.error ?? null);
  }, []);

  const handleError = useCallback((data: any) => {
    const code = data.code ?? "UNKNOWN_ERROR";

    setError({
      code,
      message: data.message ?? "An unknown error occurred",
      action: data.action,
    });

    const occurrence = (healthErrorCodeCounts.get(code) ?? 0) + 1;
    healthErrorCodeCounts.set(code, occurrence);

    console.error(`Native health error: ${code}`, null, {
      phase: "nativeHealthSDK",
      errorCode: code,
      errorMessage: data.message ?? "An unknown error occurred",
      action: data.action ?? null,
      occurrence,
      isAvailable: prevStatusRef.current.isAvailable,
      isConnected: prevStatusRef.current.isConnected,
      isSamsungHealthInstalled: data.isSamsungHealthInstalled ?? null,
    });
  }, []);

  useEffect(() => {
    if (!NativeBridgeAvailable) {
      return;
    }

    const events = {
      [BridgeEvent.HealthStatusUpdate]: handleStatusUpdate,
      [BridgeEvent.HealthDataReceived]: handleDataReceived,
      [BridgeEvent.HealthPermissionsResult]: handlePermissionsResult,
      [BridgeEvent.HealthError]: handleError,
    };

    return registerEvents(events);
  }, [
    handleStatusUpdate,
    handleDataReceived,
    handlePermissionsResult,
    handleError,
  ]);

  useEffect(() => {
    if (NativeBridgeAvailable) {
      sendEvent({
        name: BridgeEvent.SendData,
        data: {
          name: BridgeEvent.HealthGetStatus,
        },
      });
    }
  }, []);

  const requestPermissions = useCallback((dataTypes?: SDKDataType[]) => {
    if (!NativeBridgeAvailable) {
      setError({
        code: "BRIDGE_UNAVAILABLE",
        message: "Native bridge is not available",
      });
      return;
    }

    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthRequestPermissions,
        data: { dataTypes },
      },
    });
  }, []);

  const fetchHealthData = useCallback((options?: FetchHealthDataOptions) => {
    if (!NativeBridgeAvailable) {
      setError({
        code: "BRIDGE_UNAVAILABLE",
        message: "Native bridge is not available",
      });
      return;
    }

    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthFetchData,
        data: {
          dataTypes: options?.dataTypes,
          startDate: options?.startDate?.toISOString(),
          endDate: options?.endDate?.toISOString(),
          limit: options?.limit,
        },
      },
    });
  }, []);

  const refreshStatus = useCallback(() => {
    if (!NativeBridgeAvailable) {
      return;
    }

    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthGetStatus,
      },
    });
  }, []);

  const openHealthConnectSettings = useCallback(() => {
    if (!NativeBridgeAvailable) {
      return;
    }

    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthOpenSettings,
      },
    });
  }, []);

  const openAppPermissionSettings = useCallback(() => {
    if (!NativeBridgeAvailable) {
      return;
    }

    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthOpenAppSettings,
      },
    });
  }, []);

  const revokeAllPermissions = useCallback(() => {
    if (!NativeBridgeAvailable) {
      return;
    }

    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthRevokePermissions,
      },
    });
  }, []);

  const getRecordsByType = useCallback(
    (dataType: SDKDataType): HealthRecord[] => {
      return records.filter((record) => record.dataType === dataType);
    },
    [records]
  );

  const getLatestRecord = useCallback(
    (dataType: SDKDataType): HealthRecord | null => {
      const typeRecords = records
        .filter((record) => record.dataType === dataType)
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

      return typeRecords[0] ?? null;
    },
    [records]
  );

  const clearRecords = useCallback(() => {
    setRecords([]);
    setError(null);
  }, []);

  return {
    // Platform state
    status,
    isAvailable: status?.isAvailable ?? false,
    isConnected:
      status?.isConnected ?? status?.permissions?.hasAnyGranted ?? false,
    hasAnyPermissions: status?.permissions?.hasAnyGranted ?? false,
    isLoading: status?.isLoading ?? false,
    permissions: status?.permissions ?? null,
    error,
    isSamsungHealthInstalled: status?.isSamsungHealthInstalled ?? false,
    // Data state
    records,
    isFetching: status?.isFetching ?? false,
    // Actions
    requestPermissions,
    fetchHealthData,
    refreshStatus,
    openHealthConnectSettings,
    openAppPermissionSettings,
    revokeAllPermissions,
    // Helpers
    getRecordsByType,
    getLatestRecord,
    clearRecords,
  };
}

export default useNativeHealthSDK;
